import { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import { ActionSelections } from '../../../core/types/chat.types'
import {
  YEAR_MAX,
  YEAR_MIN,
  isValidMonth,
  isValidYear,
} from '../../../core/utils/explore-parameters'

type ExploreMonthYearFieldsProps = {
  initialMonth?: number
  initialYear?: number
  onChange: (selections: Partial<ActionSelections>, isValid: boolean) => void
}

const MONTH_OPTIONS = [
  { code: 1, label: 'January' },
  { code: 2, label: 'February' },
  { code: 3, label: 'March' },
  { code: 4, label: 'April' },
  { code: 5, label: 'May' },
  { code: 6, label: 'June' },
  { code: 7, label: 'July' },
  { code: 8, label: 'August' },
  { code: 9, label: 'September' },
  { code: 10, label: 'October' },
  { code: 11, label: 'November' },
  { code: 12, label: 'December' },
]

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: 'var(--ds-fg-secondary, #535b6c)',
    margin: 0,
  },
  input: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 500,
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    border: '1px solid var(--ds-border-default, #d7dae4)',
    color: 'var(--ds-fg-primary, #1f2430)',
    transition: 'all 200ms ease',
    outline: 'none',
    fontFamily: "'Poppins', sans-serif",
    '&:hover': { background: 'var(--ds-bg-subtle-hover, #e7eaf1)' },
    '&:focus': { borderColor: 'var(--color-coral-sender, #E8735A)', boxShadow: '0 0 0 2px rgba(232,115,90,0.12)' },
  },
  select: {
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
  },
}))

const ExploreMonthYearFields = ({
  initialMonth,
  initialYear,
  onChange,
}: ExploreMonthYearFieldsProps) => {
  const classes = useStyles()

  const [selectedMonth, setSelectedMonth] = useState<number | ''>(
    isValidMonth(initialMonth) ? (initialMonth as number) : ''
  )
  const [selectedYear, setSelectedYear] = useState<number | ''>(
    isValidYear(initialYear) ? (initialYear as number) : ''
  )

  useEffect(() => {
    const monthOk = selectedMonth === '' || (selectedMonth >= 1 && selectedMonth <= 12)
    const yearOk =
      selectedYear !== '' && selectedYear >= YEAR_MIN && selectedYear <= YEAR_MAX
    const isValid = monthOk && yearOk

    const selections: Partial<ActionSelections> = {
      yearChosen: selectedYear !== '' ? (selectedYear as number) : undefined,
      ...(selectedMonth !== '' ? { monthChosen: selectedMonth as number } : {}),
    }

    onChange(selections, isValid)
  }, [selectedMonth, selectedYear, onChange])

  return (
    <div className={classes.root}>
      <div className={classes.field}>
        <label className={classes.label}>Month (optional)</label>
        <select
          className={`${classes.input} ${classes.select}`}
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">Select month</option>
          {MONTH_OPTIONS.map((m) => (
            <option key={m.code} value={m.code}>{m.label}</option>
          ))}
        </select>
      </div>

      <div className={classes.field}>
        <label className={classes.label}>Year</label>
        <input
          type="number"
          className={classes.input}
          value={selectedYear}
          min={YEAR_MIN}
          max={YEAR_MAX}
          placeholder="YYYY"
          onChange={(e) => setSelectedYear(e.target.value === '' ? '' : Number(e.target.value))}
        />
      </div>
    </div>
  )
}

export default ExploreMonthYearFields
